import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import { Table, createDataSource, useDatasource } from "components/Bable"
import { fetchBillingTypes, patchBillingType } from "api/billingtypes"
import { useMutation, useQuery } from "react-query"

import { ApiError } from "api/http/utils"
import { BillingType } from "./BillingTypes/types"
import { COLUMNS } from "../columns"
import { Loading } from "components/Loading"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

const getRowId = (x: BillingType) => x.id

export const globalState = createDataSource({ getRowId })

const BillingTypes = () => {
  const { t } = useI18n()

  const { hasAuthority } = useAuthenticated()

  const [searchValue] = useState<string>("")

  const actionAllowed = hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])

  useEffect(() => globalState.setErrors({}), [])

  // ** fetch billing-types
  const { isLoading } = useQuery("market-billing-types", fetchBillingTypes, {
    onSuccess: (d: BillingType[]) => {
      globalState.setData(d)
    },
  })

  // ** patch billingtype inputs
  const [patch] = useMutation(patchBillingType, {
    onSuccess: (d: BillingType) => globalState.updateRow(d),
    onError: (e: ApiError, mf: Partial<BillingType>) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!mf.id && globalState.setRowErrors(mf.id, errorMap)
    },
  })

  const onUpdate = useCallback(
    async (row, label, value) => {
      patch({ id: row.id, [label]: value })
    },
    [patch],
  )

  const source = useDatasource({
    state: globalState,
    filters: [
      {
        active: !!searchValue.trim(),
        predicate: (row) => {
          const reg = new RegExp(searchValue, "ig")
          return reg.test(row?.name || "")
        },
      },
    ],
  })
  const context = { onUpdate }

  return (
    <contextOptions.Provider value={context}>
      <div className={styles.tittle}>
        <strong>{t("admin.billingTypes.tittle")}</strong>
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <Table<BillingType>
          columns={COLUMNS(actionAllowed)}
          source={source}
          height={200}
          className={styles.table}
        />
      )}
    </contextOptions.Provider>
  )
}
export default BillingTypes

const contextOptions = createContext<any | undefined>(undefined)

export const useContextOptions = () => {
  const options = useContext(contextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
      &[data-column-id="billingSubtypes"],
      &[data-column-id="semanticName"] {
        display: flex;
        flex: 1;
        justify-content: flex-start;
        padding-left: var(--spacing-s);
      }
      display: flex;
      flex: 1;
      justify-content: flex-start;
      padding-left: var(--spacing-xs);
    }

    .BodyBody-2BlackRegular {
      margin: 0;
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  section: css`
    font-size: 14px;
    color: var(--black);
    margin-top: 10px;
    margin-bottom: 65px;
    > * {
      margin-bottom: 23px;
    }
  `,
  tittle: css`
    font-size: 14px;
    color: var(--black);
    margin-top: 20px;
    margin-bottom: 16px;
    > * {
      margin-bottom: 1px;
    }
  `,
  label: css`
    font-size: 14px;
    color: var(--black);
    margin-top: 20px;
    > * {
      margin-bottom: 3px;
    }
  `,
}
